/**
 * Commercial terms for one customer. Talks directly to GET/POST
 * avrio-backend's /api/customers/{customerId}/plan - Super Admin only,
 * unlike the thresholds/branding form on the same page: commercial terms
 * are Avrio-internal information, not something a customer's own Admin
 * should see or set. Not rendered at all unless the caller already knows
 * the viewer is a Super Admin (CustomerSettings decides that, not this).
 *
 * A self-serve customer gets one of these rows automatically from Stripe
 * checkout; an admin-provisioned customer (the create-customer flow on
 * this same page) got none until this existed - there was no way to
 * record what was commercially agreed for an enterprise deal at all.
 *
 * Recording terms always POSTs a new row - CustomerPlanEntity is
 * append-only server-side (a renegotiated contract is a new agreement,
 * never an edit of the old one), so this form works identically whether
 * the customer has never had terms or already has several: fill it in,
 * submit, the newly recorded terms become what's displayed as current.
 */

import { memo, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Spin,
  Typography,
} from 'antd';
import axios from '../../../../axiosConfig';
import { CustomerPlan } from '../../types';
import styles from './index.module.css';

const { Text, Title } = Typography;

export interface CustomerPlanSectionProps {
  customerId: number;
  customerName: string;
}

interface FormValues {
  ratePerHire?: number;
  contractValue?: number;
  downPayment?: number;
  currency: string;
}

const formatMoney = (
  amount: number | undefined,
  currency: string | undefined,
) => {
  if (amount === undefined) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency ?? 'USD',
    }).format(amount);
  } catch {
    return `${amount} ${currency ?? ''}`.trim();
  }
};

const CustomerPlanSection = ({
  customerId,
  customerName,
}: CustomerPlanSectionProps) => {
  const [plan, setPlan] = useState<CustomerPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Separate from `error` (a failed *record*): a failed load must never read as
  // "no terms recorded", because nothing is known about this customer's terms.
  const [loadFailed, setLoadFailed] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recorded, setRecorded] = useState(false);
  const [form] = Form.useForm<FormValues>();

  const load = useCallback(() => {
    let cancelled = false;
    setPlan(null);
    setError(null);
    setLoadFailed(false);
    setRecorded(false);
    setLoading(true);
    axios
      .get<CustomerPlan>(`/api/customers/${customerId}/plan`)
      .then((res) => {
        if (cancelled) return;
        if (typeof res.data !== 'object' || res.data === null) {
          setLoadFailed(true);
          return;
        }
        setPlan(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        // No terms have ever been recorded for this customer - a normal
        // state for a freshly onboarded one, not an error to surface.
        if (err?.response?.status !== 404) setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  useEffect(() => load(), [load]);

  const handleRecord = useCallback(
    (values: FormValues) => {
      setRecording(true);
      setError(null);
      setRecorded(false);
      axios
        .post<CustomerPlan>(`/api/customers/${customerId}/plan`, values)
        .then((res) => {
          setPlan(res.data);
          setRecorded(true);
          form.resetFields();
        })
        .catch(() => setError('Could not record these terms. Try again.'))
        .finally(() => setRecording(false));
    },
    [customerId, form],
  );

  return (
    <div className={styles.section}>
      <Title level={5} className={styles.sectionTitle}>
        Plan &amp; billing terms
      </Title>
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          className={styles.banner}
        />
      )}
      {loading && (
        <div className={styles.loading}>
          <Spin size="small" />
          <Text type="secondary">Loading commercial terms…</Text>
        </div>
      )}
      {!loading && loadFailed && (
        <Alert
          type="error"
          showIcon
          message="Could not load this customer’s commercial terms."
          description="Nothing is known about their terms until this loads, so recording new ones is turned off."
          action={
            <Button size="small" onClick={load}>
              Try again
            </Button>
          }
          className={styles.banner}
        />
      )}
      {!loading && !loadFailed && (
        <>
          <div className={styles.current}>
            {plan ? (
              <>
                <Text strong>Current terms</Text>
                <dl className={styles.terms}>
                  <dt>Rate per hire</dt>
                  <dd>{formatMoney(plan.ratePerHire, plan.currency)}</dd>
                  <dt>Contract value</dt>
                  <dd>{formatMoney(plan.contractValue, plan.currency)}</dd>
                  <dt>Down payment</dt>
                  <dd>{formatMoney(plan.downPayment, plan.currency)}</dd>
                  <dt>Effective from</dt>
                  <dd>{new Date(plan.effectiveFrom).toLocaleDateString()}</dd>
                </dl>
              </>
            ) : (
              <Text type="secondary">
                No commercial terms have been recorded for {customerName} yet.
              </Text>
            )}
          </div>

          <Text type="secondary" className={styles.sectionIntro}>
            Recording new terms below adds a fresh agreement - it never edits
            the terms shown above, the same way a contract renewal doesn&rsquo;t
            rewrite the old one.
          </Text>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleRecord}
            className={styles.form}
          >
            <Form.Item
              name="currency"
              label="Currency"
              rules={[{ required: true, message: 'A currency is required' }]}
              initialValue="USD"
            >
              <Input placeholder="USD" style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="ratePerHire" label="Rate per hire">
              <InputNumber min={0} style={{ width: 200 }} />
            </Form.Item>
            <Form.Item name="contractValue" label="Contract value">
              <InputNumber min={0} style={{ width: 200 }} />
            </Form.Item>
            <Form.Item name="downPayment" label="Down payment">
              <InputNumber min={0} style={{ width: 200 }} />
            </Form.Item>
            {recorded && (
              <Alert
                type="success"
                showIcon
                message="Recorded"
                className={styles.banner}
              />
            )}
            <Button type="primary" htmlType="submit" loading={recording}>
              Record terms
            </Button>
          </Form>
        </>
      )}
    </div>
  );
};

export default memo(CustomerPlanSection);
